import * as React from 'react';
import furoApi from '../api/FuroApi';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';

import {ErrorResponse, RentalSummary, RentalSummaryRequest, RentalSummaryResponse} from '../api/dtos/dtos';
import {ColumnProps} from 'antd/lib/table';
import locales from '../locales';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import * as paths from '../paths';
import {DatePicker, Table} from 'antd';
import * as moment from 'moment';
import {ErrorAlert} from '../common/ErrorAlert';
import * as formUtils from '../utils/form-utils';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableColumn, searchableSortableColumn, sortableColumn} from '../utils/table-utils';
import * as routerUtils from '../utils/router-utils';
import FormItem from 'antd/lib/form/FormItem';
import {MOMENT_YEAR_MONTH_FORMAT} from '../utils/date-utils';
import {alignRightNoWrap, groupNumber, renderPriceColumn} from '../utils/number-utils';
import {renderStateSpan, RentalState} from '../common/state';
import ClientName from "../common/ClientName";

const {MonthPicker} = DatePicker;


interface SummaryPageState extends GridState<RentalSummary> {
  sum?: number;
  error?: ErrorResponse;
  selectedYear: number;
  selectedMonth: number;
}

class RentalSummaryPage extends React.Component<RouteComponentProps, SummaryPageState> {

  public state: SummaryPageState = {
    // Default initial grid state
    ...tableUtils.initialGridState(),

    // Initial sorting
    sortOrder: 'ascend',
    sortColumnKey: 'name',

    // Month picker default
    selectedYear: moment().get('year'),
    selectedMonth: moment().get('month'),

    // Override with cached state from Router
    ...this.props.location.state
  };

  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {selectedYear, selectedMonth, error, sum} = this.state;

    const columns: Array<ColumnProps<RentalSummary>> = [
      searchableSortableColumn(locales.client, 'name', this.state, this.renderNameColumn),
      searchableColumn(locales.responsible_user, 'responsibleUser', this.state, r => r.client?.responsibleUser),
      {
        ...sortableColumn(locales.entries, 'items', this.state, this.renderItemsColumn),
        className: 'align-right',
      }, {
        ...sortableColumn(locales.unfinalized, 'nonFinalized', this.state, this.renderNonFinalizedColumn),
        className: 'align-right',
      }
    ];

    const monthPickerDisabledDateFn = (current: moment.Moment | null): boolean => {
      return current !== null && current.isAfter(moment(), 'month');
    };

    const monthPickerValue = moment().year(selectedYear).month(selectedMonth);

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <FormItem {...formUtils.formItemLayout} label={locales.month_capitalized}>
          <MonthPicker
            format={MOMENT_YEAR_MONTH_FORMAT}
            value={monthPickerValue}
            disabledDate={monthPickerDisabledDateFn}
            onChange={this.onMonthPickerChange}
            placeholder={locales.choose}
            allowClear={false}
          />
        </FormItem>

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.indexRowKeyFn}
          bordered={true}
          size="small"
          pagination={{
            ...tableUtils.defaultPaginationConfig(this.state),
            showTotal: (total) => this.renderTotalLabel(total, sum)
          }}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 576}}
        />

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private renderTotalLabel = (total: number, sum: number | undefined): React.ReactNode => {
    return (
      <div>
        {locales.total} <b>{total}</b> {locales.entry}<br/>
        <b>{renderPriceColumn(sum)}</b>
      </div>
    );
  };

  private renderNameColumn = (r: RentalSummary): React.ReactNode => {
    return r.client && (
      <ClientName
        name={r.client.name}
        link={paths.clientSummaryPath(r.client.id!, this.state.selectedYear, this.state.selectedMonth)}
        warning={r.client.warning}
      />
    );
  };

  private renderItemsColumn = (r: RentalSummary): React.ReactNode => {
    return alignRightNoWrap(`${groupNumber(r.items)} ${locales.pcs}`);
  };

  private renderNonFinalizedColumn = (r: RentalSummary): React.ReactNode => {
    const state = r.nonFinalized === 0 ? RentalState.AVAILABLE : RentalState.RENTED;
    return alignRightNoWrap(
      <>
        {groupNumber(r.nonFinalized)} {locales.pcs} {renderStateSpan(state)}
      </>
    );
  };

  private onGridChange = (newState: GridState<RentalSummary>) => {
    this.setState({...newState}, () => this.reload());
  };

  private onMonthPickerChange = (date: moment.Moment) => {
    this.setState({
      selectedYear: date.get('year'),
      selectedMonth: date.get('month'),
    }, () => this.reload());
  };

  private reload = () => {
    const {selectedYear, selectedMonth} = this.state;
    const request: RentalSummaryRequest = {
      grid: {
        ...tableUtils.toGridRequest(this.state),
      },
      year: selectedYear,
      month: selectedMonth,
    };
    const promise = furoApi.summaries.getRentalSummaries(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: RentalSummaryResponse) => {
    this.setState({
      sum: response.sum,
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };
}

export default withRouter(RentalSummaryPage);



// WEBPACK FOOTER //
// ./src/summary/RentalSummaryPage.tsx